import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '@/components/ui/icon.js';
import {
  ANNOUNCEMENT_QUERY_KEYS,
  fetchAnnouncements,
  markSeen,
  readSeen,
  type Announcement,
} from './api.js';

/**
 * Top-of-lobby announcement strip. Pinned announcements always show; non-pinned
 * are dismissible per browser session (sessionStorage). Shows the single
 * highest-priority item (pinned first, then newest) — the bell holds the rest.
 */
export function AnnouncementsBanner() {
  const { t } = useTranslation('common');
  const [seen, setSeen] = useState<string[]>(() => readSeen());

  const { data } = useQuery({
    queryKey: ANNOUNCEMENT_QUERY_KEYS.active,
    queryFn: fetchAnnouncements,
    staleTime: 60_000,
  });

  const current = useMemo<Announcement | undefined>(() => {
    const items = [...(data ?? [])].sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return b.publishFrom.localeCompare(a.publishFrom);
    });
    return items.find((a) => a.pinned || !seen.includes(a.id));
  }, [data, seen]);

  if (!current) return null;

  const dismiss = () => {
    const next = [...seen, current.id];
    setSeen(next);
    markSeen(next);
  };

  return (
    <div className="flex items-start gap-3 rounded-2xl border border-[var(--border)] bg-[var(--bg-raised)] px-4 py-3">
      <span className="mt-0.5 shrink-0 text-[var(--accent-strong)]">
        <Icon name="bell" size={18} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[14px] font-semibold text-[var(--text-primary)]">{current.title}</div>
        <p className="mt-0.5 whitespace-pre-line text-[13px] leading-snug text-[var(--text-secondary)] line-clamp-2">
          {current.body}
        </p>
      </div>
      {!current.pinned && (
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('common.close')}
          className="shrink-0 rounded-full p-1 text-[var(--text-muted)] transition-colors hover:bg-[var(--bg-elevated)]/60 hover:text-[var(--text-primary)]"
        >
          <Icon name="close" size={16} />
        </button>
      )}
    </div>
  );
}
